import type { BasemapRasterRules, BasemapVariable } from "@pax/basemap";

/**
 * Compiles a basemap's appearances into GLSL, so every screen pixel is painted on the GPU the way
 * the format defines: `get`, `var`, `theme`, `match`, `interpolate`, `step`, `case`, `mix`, and the
 * math and logic operators. Colors are sRGB `vec4`s; numbers are `float`s; booleans are `bool`s.
 */

type ValueType = "float" | "bool" | "color";

export interface Compiled {
  code: string;
  type: ValueType;
}

export interface Scope {
  rasters: Record<string, { index: number; type: BasemapRasterRules["type"] }>;
  variables: Record<string, BasemapVariable>;
  expressions: Record<string, unknown>;
}

class ExpressionError extends Error {}

const float = (value: number) => {
  const text = String(value);
  return /[.e]/.test(text) ? text : `${text}.0`;
};

function colorLiteral(hex: string): string {
  const match = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(hex);
  if (!match?.[1]) throw new ExpressionError(`${hex} isn't a color.`);
  const channel = (at: number) =>
    float(Number.parseInt(match[1]?.slice(at, at + 2) ?? "0", 16) / 255);
  const alpha = match[2] ? float(Number.parseInt(match[2], 16) / 255) : "1.0";
  return `vec4(${channel(0)}, ${channel(2)}, ${channel(4)}, ${alpha})`;
}

/** A uniform name for a variable. */
export const variableUniform = (name: string) => `u_var_${name}`;

export function compile(expression: unknown, scope: Scope, inside: string[] = []): Compiled {
  if (typeof expression === "number") return { code: float(expression), type: "float" };
  if (typeof expression === "boolean") return { code: String(expression), type: "bool" };
  if (typeof expression === "string") return { code: colorLiteral(expression), type: "color" };
  if (!Array.isArray(expression) || typeof expression[0] !== "string") {
    throw new ExpressionError(`${JSON.stringify(expression)} isn't an expression.`);
  }
  const [operator, ...args] = expression as [string, ...unknown[]];
  const at = (index: number) => compile(args[index], scope, inside);
  const all = () => args.map(arg => compile(arg, scope, inside));

  switch (operator) {
    case "get":
      return read(String(args[0]), scope);
    case "var": {
      const name = String(args[0]);
      if (name in scope.expressions) {
        if (inside.includes(name)) throw new ExpressionError(`${name} reads itself.`);
        return compile(scope.expressions[name], scope, [...inside, name]);
      }
      const variable = scope.variables[name];
      if (!variable) throw new ExpressionError(`There's no ${name} to read.`);
      return { code: variableUniform(name), type: variable.type === "boolean" ? "bool" : "float" };
    }
    case "theme": {
      const [light, dark] = [at(0), at(1)];
      return { code: `(u_dark ? ${dark.code} : ${light.code})`, type: light.type };
    }
    case "match": {
      const input = at(0);
      const fallback = compile(args.at(-1), scope, inside);
      let code = fallback.code;
      for (let index = args.length - 3; index >= 1; index -= 2) {
        const labels = Array.isArray(args[index]) ? (args[index] as number[]) : [args[index]];
        const test = labels.map(label => `${input.code} == ${float(Number(label))}`).join(" || ");
        code = `((${test}) ? ${compile(args[index + 1], scope, inside).code} : ${code})`;
      }
      return { code, type: fallback.type };
    }
    case "interpolate": {
      const how = (args[0] as [string])[0];
      const input = at(1);
      const stops: [number, Compiled][] = [];
      for (let index = 2; index < args.length; index += 2) {
        stops.push([Number(args[index]), compile(args[index + 1], scope, inside)]);
      }
      const last = stops.at(-1);
      if (!last) throw new ExpressionError("interpolate needs stops.");
      let code = last[1].code;
      for (let index = stops.length - 2; index >= 0; index--) {
        const [from, low] = stops[index] as [number, Compiled];
        const [to, high] = stops[index + 1] as [number, Compiled];
        const share = `clamp((${input.code} - ${float(from)}) / ${float(to - from)}, 0.0, 1.0)`;
        const eased = how === "smooth" ? `smoothstep(0.0, 1.0, ${share})` : share;
        code = `(${input.code} < ${float(to)} ? mix(${low.code}, ${high.code}, ${eased}) : ${code})`;
      }
      return { code, type: last[1].type };
    }
    case "step": {
      const input = at(0);
      let code = "";
      const outputs = [at(1)];
      const edges: number[] = [];
      for (let index = 2; index < args.length; index += 2) {
        edges.push(Number(args[index]));
        outputs.push(compile(args[index + 1], scope, inside));
      }
      code = outputs.at(-1)?.code ?? "0.0";
      for (let index = edges.length - 1; index >= 0; index--) {
        code = `(${input.code} < ${float(edges[index] ?? 0)} ? ${outputs[index]?.code} : ${code})`;
      }
      return { code, type: outputs[0]?.type ?? "float" };
    }
    case "case": {
      const fallback = compile(args.at(-1), scope, inside);
      let code = fallback.code;
      for (let index = args.length - 3; index >= 0; index -= 2) {
        code = `(${at(index).code} ? ${at(index + 1).code} : ${code})`;
      }
      return { code, type: fallback.type };
    }
    case "coalesce":
      return at(0);
    case "mix":
    case "mix-oklab": {
      const [a, b, t] = [at(0), at(1), at(2)];
      return { code: `mix(${a.code}, ${b.code}, clamp(${t.code}, 0.0, 1.0))`, type: a.type };
    }
    case "rgb": {
      const [r, g, b] = all();
      return {
        code: `vec4(${r?.code} / 255.0, ${g?.code} / 255.0, ${b?.code} / 255.0, 1.0)`,
        type: "color",
      };
    }
    case "to-linear":
      return { code: `toLinear(${at(0).code})`, type: "color" };
    case "to-srgb":
      return { code: `toSrgb(${at(0).code})`, type: "color" };
    case "luminance":
      return {
        code: `dot(toLinear(${at(0).code}).rgb, vec3(0.2126, 0.7152, 0.0722))`,
        type: "float",
      };
    case "slope":
    case "aspect": {
      const raster = scope.rasters[String(args[0])];
      if (raster?.type !== "elevation") {
        throw new ExpressionError(`${operator} reads an elevation raster.`);
      }
      return { code: `${operator}Of${raster.index}(direction)`, type: "float" };
    }
    case "+":
    case "*":
    case "min":
    case "max": {
      const values = all();
      const type = values.some(value => value.type === "color") ? "color" : "float";
      const code = values
        .map(value => value.code)
        .reduce((left, right) =>
          operator === "min" || operator === "max"
            ? `${operator}(${left}, ${right})`
            : `(${left} ${operator} ${right})`,
        );
      return { code, type };
    }
    case "-": {
      if (args.length === 1) return { code: `(-${at(0).code})`, type: at(0).type };
      const [a, b] = [at(0), at(1)];
      return {
        code: `(${a.code} - ${b.code})`,
        type: a.type === "color" || b.type === "color" ? "color" : "float",
      };
    }
    case "/":
      return { code: `(${at(0).code} / ${at(1).code})`, type: at(0).type };
    case "%":
      return { code: `mod(${at(0).code}, ${at(1).code})`, type: "float" };
    case "^":
      return { code: `pow(${at(0).code}, ${at(1).code})`, type: at(0).type };
    case "abs":
    case "floor":
    case "ceil":
    case "round":
    case "sqrt":
    case "exp":
    case "sin":
    case "cos":
      return { code: `${operator}(${at(0).code})`, type: at(0).type };
    case "ln":
      return { code: `log(${at(0).code})`, type: "float" };
    case "log2":
      return { code: `log2(${at(0).code})`, type: "float" };
    case "log10":
      return { code: `(log(${at(0).code}) / 2.302585)`, type: "float" };
    case "clamp":
      return { code: `clamp(${at(0).code}, ${at(1).code}, ${at(2).code})`, type: at(0).type };
    case "smoothstep":
      return { code: `easedStep(${at(0).code}, ${at(1).code}, ${at(2).code})`, type: "float" };
    case "linearstep":
      return { code: `linearStep(${at(0).code}, ${at(1).code}, ${at(2).code})`, type: "float" };
    case "==":
    case "!=":
    case "<":
    case "<=":
    case ">":
    case ">=":
      return { code: `(${at(0).code} ${operator} ${at(1).code})`, type: "bool" };
    case "!":
      return { code: `(!${at(0).code})`, type: "bool" };
    case "all":
      return {
        code: `(${all()
          .map(value => value.code)
          .join(" && ")})`,
        type: "bool",
      };
    case "any":
      return {
        code: `(${all()
          .map(value => value.code)
          .join(" || ")})`,
        type: "bool",
      };
    default:
      throw new ExpressionError(`${operator} isn't an operator this map draws.`);
  }
}

function read(input: string, scope: Scope): Compiled {
  const raster = scope.rasters[input];
  if (raster) {
    return raster.type === "color"
      ? { code: `colorOf${raster.index}(direction)`, type: "color" }
      : { code: `valueOf${raster.index}(direction)`, type: "float" };
  }
  switch (input) {
    case "latitude":
      return { code: "latitudeOf(direction)", type: "float" };
    case "metersPerPixel":
      return { code: "g_metersPerPixel", type: "float" };
    case "dark":
      return { code: "u_dark", type: "bool" };
    case "preview":
      return { code: "true", type: "bool" };
    default:
      throw new ExpressionError(`There's no raster named ${input}.`);
  }
}

/** A value as a color, for a step's `color` output. */
export function asColor(value: Compiled): string {
  if (value.type === "color") return value.code;
  if (value.type === "float") return `vec4(vec3(${value.code}), 1.0)`;
  return `(${value.code} ? vec4(1.0) : vec4(0.0, 0.0, 0.0, 1.0))`;
}

/** A value as a number, for a step's `opacity` output. */
export function asNumber(value: Compiled): string {
  if (value.type === "float") return value.code;
  if (value.type === "bool") return `(${value.code} ? 1.0 : 0.0)`;
  return `${value.code}.a`;
}
